import Vue from 'vue';
import {
    fetchLabels,
    createLabel,
    createLabelOnEntity,
    addLabelToEntity,
    removeLabelFromEntity,
    deleteLabel,
    getLabel,
    editLabel,
    createPublicLabel,
} from './labels.utils';
import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import keyBy from 'lodash/keyBy';

const PRIVATE_ICON = { stroke: '#2a2c35', size: 10, strokeWidth: 3, type: 'lock' };

export default {
    namespaced: true,
    state: {
        map: {},
        entityMap: {},
        fetching: false,
        saving: false,
        error: null,
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { id, label }) {
            if (label.isPrivate) label.icon = PRIVATE_ICON;
            Vue.set(state.map, id, label);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setSaving (state, { saving }) {
            state.saving = saving;
        },
        // entityMap mutations
        setEntityMap (state, { entityMap }) {
            state.entityMap = entityMap;
        },
        setEntityMapAtKey (state, { id, labels }) {
            Vue.set(state.entityMap, id, labels);
        },
        updateEntityMap (state, { updates }) {
            state.entityMap = Object.assign({}, state.entityMap, updates);
        },
        addLabelsToEntityInMap (state, { id, labelIds }) {
            Vue.set(state.entityMap, id, (state.entityMap[id] || []).concat(labelIds));
        },
        removeLabelsFromEntityInMap (state, { id, labelIds }) {
            const remainingLabels = state.entityMap[id].filter((labelId) => !labelIds.includes(labelId));
            Vue.set(state.entityMap, id, remainingLabels);
        },
        removeFromMap (state, { labelId }) {
            Vue.delete(state.map, labelId);
        },
    },
    actions: {
        async loadAll ({ commit, state }, { reFetch = false } = {}) {
            if (!isEmpty(state.map) && !reFetch) return Promise.resolve();

            commit('setFetching', { fetching: true });
            try {
                const response = await fetchLabels();
                const { data: labels } = response;

                commit('setFetching', { fetching: false });
                commit('setMap', {
                    map: keyBy(
                        labels.map((label) => {
                            if (label.isPrivate) label.icon = PRIVATE_ICON;

                            return label;
                        }),
                        'id',
                    ),
                });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async loadLabelById ({ commit }, { labelId } = {}) {
            commit('setFetching', { fetching: true });
            try {
                const response = await getLabel(labelId);
                const { data: label } = response;

                commit('setFetching', { fetching: false });

                return label;
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async editLabel ({ commit }, { labelId, labelName, isPrivate }) {
            commit('setSaving', { saving: true });

            try {
                const result = await editLabel({ labelId, labelName });
                const label = result.data;
                label.isPrivate = isPrivate;

                commit('setMapAtKey', {
                    id: label.id,
                    label,
                });
                commit('setSaving', { saving: false });

                return label;
            } catch (error) {
                commit('setSaving', { saving: false });
                commit('setError', { error });
                throw error;
            }
        },
        async create ({ commit }, { name, isPrivate }) {
            commit('setSaving', { saving: true });

            try {
                const result = await createLabel(name);
                const label = { ...result.data, isPrivate };

                if (!isPrivate) {
                    await createPublicLabel(label.id);
                }

                commit('setMapAtKey', {
                    id: label.id,
                    label,
                });
                commit('setSaving', { saving: false });

                return label;
            } catch (error) {
                commit('setSaving', { saving: false });
                commit('setError', { error });
                throw error;
            }
        },
        async createOnEntity ({ commit }, { name, isPrivate, memberId, memberType }) {
            commit('setSaving', { saving: true });

            try {
                const result = await createLabelOnEntity(name, isPrivate, memberId, memberType);
                const label = { ...result.data, isPrivate };

                if (!isPrivate) {
                    await createPublicLabel(label.id);
                }

                commit('setMapAtKey', {
                    id: label.id,
                    label,
                });
                commit('addLabelsToEntityInMap', { id: memberId, labelIds: [label.id] });
                commit('setSaving', { saving: false });

                return label;
            } catch (error) {
                commit('setSaving', { saving: false });
                commit('setError', { error });
                throw error;
            }
        },
        async updateLabelsOnEntity ({ state, commit }, { addLabelIds, removeLabelIds, memberId, memberType }) {
            commit('setSaving', { saving: true });

            try {
                const addPromises = addLabelIds.map((labelId) => addLabelToEntity(labelId, memberId, memberType));
                const removePromises = removeLabelIds.map((labelId) =>
                    removeLabelFromEntity(labelId, memberId, memberType),
                );
                const results = await Promise.all(addPromises.concat(removePromises));
                const subsetMap = {};
                results.forEach(({ data: label }) => {
                    if (label && label.id) subsetMap[label.id] = Object.assign({}, state.map[label.id], label);
                });
                commit('setMap', { map: { ...state.map, ...subsetMap } });
                commit('addLabelsToEntityInMap', { id: memberId, labelIds: addLabelIds });
                commit('removeLabelsFromEntityInMap', { id: memberId, labelIds: removeLabelIds });
                commit('setSaving', { saving: false });
            } catch (error) {
                commit('setSaving', { saving: false });
                commit('setError', { error });
                throw error;
            }
        },
        cacheLabelsOnEntities ({ commit }, { entities }) {
            const updates = entities.reduce((map, entity) => {
                const labels = entity.labels || [];
                const ids = labels.map((label) => label.id);
                map[entity.id] = ids;

                return map;
            }, {});
            commit('updateEntityMap', { updates });
        },
        async deleteLabel ({ commit }, { labelId } = {}) {
            commit('setSaving', { saving: true });

            try {
                await deleteLabel(labelId);
                commit('removeFromMap', { labelId });
                commit('setSaving', { saving: false });
            } catch (error) {
                commit('setSaving', { saving: false });
                commit('setError', { error });
                throw error;
            }
        },
    },
    getters: {
        list: (state) => values(state.map),
        labelById: (state) => (id) => state.map[id],
        labelsOnEntity: (state) => (id) =>
            (state.entityMap[id] || []).map((labelId) => state.map[labelId]).filter((label) => label !== undefined),
        /**
         * We do not want to add guide labels to the v2 permission system. The feature is slated for deprecation.
         * The solution that supersedes this one will be added to v2 permissions. In the meantime, this feature's
         * permission check logic is kept here (outside the auth module) to aid in retiring the v1 permission model.
         */
        can: (_state, _getters, _rootState, rootGetters) => (action) => {
            if (action === 'addPublic') {
                return !rootGetters['auth/isReadOnly'];
            }

            if (action === 'delete' || action === 'edit') {
                return rootGetters['auth/hasAdminAccess'];
            }

            return false;
        },
    },
};
